import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, AlertTriangle, ShieldCheck } from "lucide-react";
import Footer from "@/components/landing/Footer";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import { Button } from "@/components/ui/button";

const LAST_UPDATED = "March 2026";

export default function LegalLayout({ title, subtitle, children, testId }) {
    const { t } = useTranslation();
    return (
        <main className="min-h-screen bg-[#FBF7EE] text-[#1B2621]" data-testid={testId || "legal-page"}>
            {/* Top bar */}
            <header className="sticky top-0 z-40 border-b border-[#E9D9C1]/70 bg-[#FBF7EE]/85 backdrop-blur-md">
                <div className="mx-auto max-w-5xl px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#062A1E] hover:text-[#0B4A34] transition-colors duration-150"
                        data-testid="legal-back-home"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span>Golf Power Play</span>
                        <span className="text-[10px] align-top text-[#1B2621]/60">™</span>
                    </Link>
                    <div className="flex items-center gap-1">
                        <LanguageSwitcher />
                    </div>
                </div>
            </header>

            <article className="mx-auto max-w-3xl px-5 sm:px-8 pt-10 sm:pt-14 pb-12">
                {/* Disclaimer banner */}
                <div
                    role="note"
                    aria-label="Not legal advice disclaimer"
                    data-testid="legal-disclaimer-banner"
                    className="mb-8 rounded-2xl border border-[#E2C66E] bg-[#FBEFC4] text-[#5B4313] p-4 sm:p-5 shadow-[0_8px_24px_rgba(170,127,33,0.12)]"
                >
                    <div className="flex items-start gap-3">
                        <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#F5E3A6] border border-[#E2C66E]">
                            <AlertTriangle className="h-4 w-4 text-[#8B6F2A]" />
                        </span>
                        <div className="text-xs sm:text-sm leading-relaxed">
                            <strong className="text-[#5B4313] block mb-1 text-sm sm:text-base">
                                Not legal advice
                            </strong>
                            <p>
                                The information on this page is provided for general informational purposes only and does not constitute legal advice. It is not intended to create, and receipt or viewing does not constitute, an attorney-client relationship. You should consult a licensed attorney for advice specific to your situation. Golf Power Play is not a law firm and disclaims all liability arising from reliance on this content.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Title block */}
                <div className="mb-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#E9D9C1] bg-[#F4EBDD]/70 px-3 py-1 text-[11px] font-semibold tracking-[0.16em] uppercase text-[#8B6F2A]">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        <span>Legal</span>
                    </div>
                    <h1
                        className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#062A1E]"
                        data-testid="legal-page-title"
                    >
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="mt-3 text-sm sm:text-base text-[#1B2621]/70 max-w-2xl">
                            {subtitle}
                        </p>
                    )}
                    <p className="mt-3 text-xs sm:text-sm text-[#1B2621]/60">
                        Last updated: {LAST_UPDATED} · Provided in English. Localized translations
                        forthcoming; if a translation is provided, the English version controls.
                    </p>
                </div>

                {/* Content */}
                <div className="legal-prose">{children}</div>

                {/* Bottom navigation */}
                <div className="mt-12 pt-8 border-t border-[#E9D9C1] flex flex-wrap gap-3 items-center justify-between">
                    <Button asChild variant="secondary" className="bg-[#F4EBDD] hover:bg-[#E9D9C1] text-[#062A1E] border border-[#E9D9C1]">
                        <Link to="/" data-testid="legal-bottom-back">
                            <ArrowLeft className="h-4 w-4 mr-1.5" />
                            Back to home
                        </Link>
                    </Button>
                    <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                        <Link to="/privacy" className="text-[#0B4A34] hover:text-[#137A55] underline underline-offset-2">
                            {t("footer.legal.privacy")}
                        </Link>
                        <Link to="/terms" className="text-[#0B4A34] hover:text-[#137A55] underline underline-offset-2">
                            {t("footer.legal.terms")}
                        </Link>
                        <Link to="/do-not-sell" className="text-[#0B4A34] hover:text-[#137A55] underline underline-offset-2">
                            Do Not Sell or Share
                        </Link>
                    </div>
                </div>
            </article>

            <Footer />
        </main>
    );
}

/* -----------------------------------------------------------------------
   Re-usable section + helpers
----------------------------------------------------------------------- */

export function LegalSection({ id, title, children }) {
    return (
        <section id={id} className="mb-9 scroll-mt-24">
            <h2 className="text-xl sm:text-2xl font-bold text-[#062A1E] mb-3">{title}</h2>
            <div className="space-y-3 text-[15px] leading-relaxed text-[#1B2621]/85">
                {children}
            </div>
        </section>
    );
}

export function LegalList({ items }) {
    return (
        <ul className="list-disc list-outside pl-5 space-y-1.5 text-[15px] leading-relaxed text-[#1B2621]/85">
            {items.map((item, i) => (
                <li key={i}>{item}</li>
            ))}
        </ul>
    );
}

export const CONTACT_EMAIL = "privacy@golfpowerplay.com";
export const ENTITY_NAME = "Golf Power Play";
